import { RoleType, UserStatus } from '@hit-game/shared/enums';
import type { IUser } from '@hit-game/shared/types';
import { Plus, Users } from 'lucide-react';
import { useCallback, useState } from 'react';

import { PageHeader } from '../../components/account/PageHeader';
import { UserCreateModal, type NewUserInput } from '../../components/admin/UserCreateModal';
import { DataStateMessage } from '../../components/common/DataStateMessage';
import { DataToolbar } from '../../components/common/DataToolbar';
import { Pagination } from '../../components/common/Pagination';
import { useConfirm } from '../../context/ConfirmContext';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import { userService } from '../../services/user.service';
import { formatDate } from '../../utils/format';

const COLUMNS = ['Tên đăng nhập', 'Vai trò', 'Ghi chú', 'Ngày tạo', 'Trạng thái'];

export function UsersPage() {
  const confirm = useConfirm();

  const fetcher = useCallback(
    (params: { page: number; limit: number; search?: string }) => userService.list(params),
    [],
  );
  const list = usePaginatedList<IUser>(fetcher);

  const [createOpen, setCreateOpen] = useState(false);

  const createUser = async (input: NewUserInput): Promise<boolean> => {
    try {
      await userService.create(input);
      list.reload();
      return true;
    } catch {
      return false; // Toast lỗi do interceptor bắn.
    }
  };

  const toggleStatus = async (user: IUser) => {
    const nextStatus = user.status === UserStatus.ACTIVE ? UserStatus.INACTIVE : UserStatus.ACTIVE;

    if (nextStatus === UserStatus.INACTIVE) {
      const accepted = await confirm({
        title: 'Khoá tài khoản',
        message: `Khoá "${user.username}"? Mọi phiên đang mở sẽ bị chặn ở request kế tiếp.`,
        confirmText: 'Khoá',
        type: 'danger',
      });
      if (!accepted) return;
    }

    try {
      await userService.updateStatus(String(user._id), nextStatus);
      list.reload();
    } catch {
      // Toast lỗi do interceptor bắn.
    }
  };

  const isEmpty = !list.loading && list.items.length === 0;

  return (
    <div className="text-ink space-y-6">
      <PageHeader
        icon={Users}
        title="Quản lý người dùng"
        subtitle="Tạo tài khoản vận hành và phân vai trò"
      />

      <div className="border-line bg-panel overflow-hidden rounded-xl border">
        <DataToolbar
          icon={<Users size={16} />}
          title="Danh sách người dùng"
          count={list.total}
          searchValue={list.search}
          onSearchChange={list.setSearch}
          searchPlaceholder="Tìm theo tên đăng nhập..."
          action={
            <button
              type="button"
              onClick={() => {
                setCreateOpen(true);
              }}
              className="bg-accent hover:bg-accent-hover ml-2 flex items-center gap-1 rounded-lg px-3 py-1.5 text-[11px] font-bold text-white transition-colors"
            >
              <Plus size={13} />
              Thêm
            </button>
          }
        />

        <DataStateMessage
          loading={list.loading}
          empty={isEmpty ? 'Chưa có người dùng' : null}
          emptyIcon={<Users size={32} className="text-line" />}
        />

        {!list.loading && list.items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="bg-canvas-raised">
                  {COLUMNS.map((column) => (
                    <th
                      key={column}
                      className="text-ink-faint px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.1em]"
                    >
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.items.map((user) => (
                  <tr
                    key={String(user._id)}
                    className="border-panel-hover hover:bg-panel-raised border-t transition-colors"
                  >
                    <td className="text-ink px-5 py-3 font-mono text-[12px] font-bold">
                      {user.username}
                    </td>
                    <td className="px-5 py-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${
                          user.role === RoleType.ADMIN
                            ? 'bg-accent-soft text-accent'
                            : 'bg-panel-hover text-ink-muted'
                        }`}
                      >
                        {user.role}
                      </span>
                    </td>
                    <td className="text-ink-muted px-5 py-3 text-[12px]">{user.note || '-'}</td>
                    <td className="text-ink-dim px-5 py-3 text-[12px]">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="px-5 py-3">
                      <button
                        type="button"
                        onClick={() => {
                          void toggleStatus(user);
                        }}
                        className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold transition-colors ${
                          user.status === UserStatus.ACTIVE
                            ? 'text-success border-success-border bg-success-bg hover:bg-success-deep border'
                            : 'text-danger border-danger-border bg-danger-bg hover:bg-danger-bg-hover border'
                        }`}
                      >
                        {user.status === UserStatus.ACTIVE ? 'Đang hoạt động' : 'Đã khoá'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <Pagination
          page={list.page}
          totalPages={list.totalPages}
          onPageChange={list.setPage}
          loading={list.loading}
        />
      </div>

      <UserCreateModal
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
        }}
        onSubmit={createUser}
      />
    </div>
  );
}
